import {
    ArrowSquareRightIcon,
    FolderIcon,
    FloppyDiskIcon,
    TrashIcon,
    FloppyDiskBackIcon,
    PlusIcon,
    CaretDownIcon,
} from '@phosphor-icons/react'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useModal, MODALS } from '../../../../modals'
import {
    ScoopTab,
    ScoopButton,
    ScoopDrawer,
    ScoopSelector,
    ScoopMenu,
    ScoopMenuItem,
    ScoopMenuDivider,
    ScoopText,
    ScoopDatePicker,
    ScoopDivider,
    ScoopSkeleton,
    ScoopColumn,
    ScoopCenter,
} from '../../../../ui'
import styles from './LeftPanel.module.scss'

const leftDrawerTabs = [
    { label: 'Data', key: 'data' },
    { label: 'Filter', key: 'filter' },
]

export const LeftPanel = ({
    diagramState,
    datasetToAnalyzeForm,
    onChangeDatasetToAnalyzeForm,
    datasetDropdownOptions,
    datasetDropdownLoading,
    columnDropdownOptions,
    valueDropdownOptions,
    onOpenLoadDiagram,
    handleRequestDiagram,
    handleFetchSankey,
    isOpen,
    setIsOpen,
    diagramSetFilterV2,
    diagramSetFilterSnapshotDate,
    diagramApplyFiltersAndSnapshotDate,
    preliminaryData,
    diagramSetStartDate,
    diagramSetEndDate,
    handleSave,
    onSaveAs,
    onDelete,
}) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedLeftTab, setSelectedLeftTab] = useState(leftDrawerTabs[0].key)
    const filterModal = useModal(MODALS.PROCESS.FILTER_SELECTION)
    const isLoadingFilters = false // TODO: Connect to actual loading state
    const open = Boolean(anchorEl)

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSaveClick = () => {
        handleSave(diagramState.diagramName)
        handleClose()
    }

    const handleSaveAsClick = () => {
        onSaveAs()
        handleClose()
    }

    const handleDeleteClick = () => {
        onDelete()
        handleClose()
    }

    const handleOpenFilterModal = async () => {
        const result = await filterModal.show({
            preliminaryData,
            diagramState,
            reportSeriesTableID: datasetToAnalyzeForm?.dataset,
        })
        if (result) {
            diagramApplyFiltersAndSnapshotDate(result.filters, result.snapshotDate)
        }
    }

    return (
        <div>
            <div className={`${styles.toggleWrapper} ${isOpen ? styles.toggleWrapperOpen : ''}`}>
                <ScoopButton
                    variant="text"
                    size="xxs"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`${styles.toggleButton} ${isOpen ? styles.toggleButtonOpen : ''}`}
                    aria-label={isOpen ? 'Close left panel' : 'Open left panel'}
                >
                    <ArrowSquareRightIcon
                        size={18}
                        weight="regular"
                        className={`${styles.toggleIcon} ${isOpen ? styles.toggleIconRotated : ''}`}
                    />
                </ScoopButton>
            </div>
            <ScoopDrawer open={isOpen} anchor="left" variant="persistent" width={300}>
                {/* Tab Selector */}
                <div className={styles.tabContainer}>
                    <ScoopTab
                        tabs={leftDrawerTabs}
                        value={selectedLeftTab}
                        onChange={(_, value) => setSelectedLeftTab(value)}
                    />
                </div>

                <ScoopColumn className={styles.contentContainer}>
                    {selectedLeftTab === 'data' ? (
                        <div className={styles.dataTabContent}>
                            <ScoopText size="sm" weight="semibold">
                                Create Diagram
                            </ScoopText>
                            {datasetDropdownLoading ? (
                                <ScoopSkeleton
                                    variant="rectangular"
                                    height={55}
                                    className={styles.skeleton}
                                />
                            ) : (
                                <ScoopSelector
                                    label="Report"
                                    value={datasetToAnalyzeForm?.dataset ?? ''}
                                    disabled={!Boolean(datasetDropdownOptions?.length)}
                                    onChange={(e) =>
                                        onChangeDatasetToAnalyzeForm(e.target?.value, 'dataset')
                                    }
                                    options={datasetDropdownOptions || []}
                                    size="sm"
                                />
                            )}
                            <ScoopSelector
                                label="Attribute"
                                value={datasetToAnalyzeForm?.attribute || ''}
                                disabled={!Boolean(datasetToAnalyzeForm?.dataset?.length)}
                                onChange={(e) =>
                                    onChangeDatasetToAnalyzeForm(e.target?.value, 'attribute')
                                }
                                options={columnDropdownOptions || []}
                                size="sm"
                            />
                            <ScoopSelector
                                label="Value"
                                value={datasetToAnalyzeForm?.value ?? ''}
                                disabled={!Boolean(datasetToAnalyzeForm?.attribute?.length)}
                                onChange={(e) =>
                                    onChangeDatasetToAnalyzeForm(e.target?.value, 'value')
                                }
                                options={valueDropdownOptions || []}
                                size="sm"
                            />
                            <ScoopDatePicker
                                label="Start Date"
                                value={
                                    diagramState?.startDate
                                        ? new Date(diagramState.startDate)
                                        : null
                                }
                                onChange={(newValue) => {
                                    if (newValue) {
                                        diagramSetStartDate(dayjs(newValue).format('MM/DD/YYYY'))
                                    }
                                }}
                                className={styles.datePicker}
                            />
                            <ScoopDatePicker
                                label="End Date"
                                value={
                                    diagramState?.endDate ? new Date(diagramState.endDate) : null
                                }
                                onChange={(newValue) => {
                                    if (newValue) {
                                        diagramSetEndDate(dayjs(newValue).format('MM/DD/YYYY'))
                                    }
                                }}
                                className={styles.datePicker}
                            />
                            <ScoopButton
                                leftSection={<PlusIcon size={18} />}
                                size="fullWidth"
                                onClick={() => {
                                    if (diagramState?.isSankey) {
                                        handleFetchSankey({
                                            isStepBased: diagramState.isStepBased,
                                            numberOfSteps: diagramState.numberOfSteps,
                                            sankeyPeriod: diagramState.sankeyPeriod,
                                            allowedStartingValues:
                                                diagramState.allowedStartingValues,
                                        })
                                    } else {
                                        handleRequestDiagram(true)
                                    }
                                }}
                                disabled={!Boolean(datasetToAnalyzeForm?.value?.length)}
                            >
                                Create
                            </ScoopButton>
                            <ScoopDivider />
                            {/* Diagram Name Section with Dropdown */}
                            <div className={styles.currentDiagramction}>
                                <ScoopText size="sm" weight="semibold">
                                    Current Diagram
                                </ScoopText>
                                <div className={styles.currentDiagramRow}>
                                    <ScoopText variant="body2" className={styles.diagramName}>
                                        {diagramState?.diagramName || 'No diagram loaded'}
                                    </ScoopText>
                                    {diagramState?.diagramName && (
                                        <>
                                            <ScoopButton variant="text" onClick={handleClick}>
                                                <CaretDownIcon size={16} weight="regular" />
                                            </ScoopButton>
                                            <ScoopMenu
                                                anchorEl={anchorEl}
                                                open={open}
                                                onClose={handleClose}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                            >
                                                <ScoopMenuItem
                                                    onClick={handleSaveClick}
                                                    leftSection={<FloppyDiskIcon size={16} />}
                                                >
                                                    Save
                                                </ScoopMenuItem>
                                                <ScoopMenuItem
                                                    onClick={handleSaveAsClick}
                                                    leftSection={<FloppyDiskBackIcon size={16} />}
                                                >
                                                    Save As
                                                </ScoopMenuItem>
                                                <ScoopMenuDivider />
                                                <ScoopMenuItem
                                                    onClick={handleDeleteClick}
                                                    leftSection={<TrashIcon size={16} />}
                                                >
                                                    Delete
                                                </ScoopMenuItem>
                                            </ScoopMenu>
                                        </>
                                    )}
                                </div>
                            </div>

                            <ScoopButton
                                leftSection={<FolderIcon size={16} color="#fff" />}
                                size="fullWidth"
                                onClick={onOpenLoadDiagram}
                            >
                                Open
                            </ScoopButton>
                        </div>
                    ) : (
                        /* Filter Tab Content */
                        <div className={styles.filterTabContent}>
                            {/* Summary Filters Section */}
                            <div className={styles.summaryFiltersSection}>
                                <ScoopText
                                    size="sm"
                                    weight="semibold"
                                    className={styles.summaryFiltersTitle}
                                >
                                    Summary filters
                                </ScoopText>
                                <div className={styles.summaryFiltersContainer}>
                                    {/* Show Snapshot Date if active */}
                                    {diagramState?.filterSnapshotDate && (
                                        <div className={styles.filterCard}>
                                            <div className={styles.filterCardContent}>
                                                <ScoopText className={styles.filterLabel}>
                                                    Snapshot Date
                                                </ScoopText>
                                                <ScoopText className={styles.filterValue}>
                                                    {diagramState.filterSnapshotDate}
                                                </ScoopText>
                                            </div>
                                            <ScoopButton
                                                variant="text"
                                                size="xs"
                                                onClick={() => diagramSetFilterSnapshotDate(null)}
                                            >
                                                <TrashIcon size={16} weight="regular" />
                                            </ScoopButton>
                                        </div>
                                    )}
                                    {isLoadingFilters ? (
                                        <div className={styles.loadingContainer}>
                                            <ScoopSkeleton
                                                variant="rectangular"
                                                height={60}
                                                className={styles.skeleton}
                                            />
                                        </div>
                                    ) : (!diagramState?.selectedFiltersV2 ||
                                          diagramState.selectedFiltersV2.length === 0) &&
                                      !diagramState?.filterSnapshotDate ? (
                                        <div className={styles.noFiltersContainer}>
                                            <ScoopText className={styles.noFiltersTitle}>
                                                No filters applied
                                            </ScoopText>
                                            <ScoopText className={styles.noFiltersDescription}>
                                                Select filters below to refine your data
                                            </ScoopText>
                                        </div>
                                    ) : (
                                        <div className={styles.filterGroupContainer}>
                                            {(() => {
                                                // Group filters by selectedFilter (column name)
                                                const groupedFilters =
                                                    diagramState.selectedFiltersV2.reduce(
                                                        (acc, filter) => {
                                                            if (!acc[filter.selectedFilter]) {
                                                                acc[filter.selectedFilter] = []
                                                            }
                                                            acc[filter.selectedFilter].push(
                                                                filter.key
                                                            )
                                                            return acc
                                                        },
                                                        {}
                                                    )

                                                return Object.entries(groupedFilters).map(
                                                    ([columnName, values]) => (
                                                        <div
                                                            key={columnName}
                                                            className={styles.filterCard}
                                                        >
                                                            <div
                                                                className={styles.filterCardContent}
                                                            >
                                                                <ScoopText
                                                                    className={styles.filterLabel}
                                                                >
                                                                    {columnName}
                                                                </ScoopText>
                                                                <ScoopText
                                                                    className={styles.filterValue}
                                                                >
                                                                    {values.join(', ')}
                                                                </ScoopText>
                                                            </div>
                                                            <ScoopButton
                                                                variant="text"
                                                                size="xs"
                                                                onClick={() => {
                                                                    // Remove all filters for this column
                                                                    const newFilters =
                                                                        diagramState.selectedFiltersV2.filter(
                                                                            (f) =>
                                                                                f.selectedFilter !==
                                                                                columnName
                                                                        )
                                                                    diagramSetFilterV2(newFilters)
                                                                }}
                                                            >
                                                                <TrashIcon
                                                                    size={16}
                                                                    weight="regular"
                                                                />
                                                            </ScoopButton>
                                                        </div>
                                                    )
                                                )
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <ScoopDivider className={styles.filterDivider} />

                            {/* Add Filter Button */}
                            <ScoopCenter className={styles.addFilterButtonContainer}>
                                <ScoopButton
                                    leftSection={<PlusIcon size={18} />}
                                    size="fullWidth"
                                    onClick={handleOpenFilterModal}
                                    disabled={!datasetToAnalyzeForm?.dataset}
                                >
                                    Add Filter
                                </ScoopButton>
                            </ScoopCenter>
                        </div>
                    )}
                </ScoopColumn>
            </ScoopDrawer>
        </div>
    )
}
